// src/pages/LittleAuthors.js

import React, { useEffect, useRef, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { Container, Row, Col, Card } from 'react-bootstrap';
import './LittleAuthors.css'; 

const AUTHORS = [
  { 
    book: "A Message from the Future", 
    img: "/schoolproject/SchoolProjectsLittleHibba1png.png", 
    color: "#4facfe",
    tag: "Program Debut!"
  },
  { 
    book: "The Responding Book", 
    img: "/schoolproject/SchoolProjectsLittleHibba2.png", 
    color: "#ff758c",
    tag: "Visionary Mind!"
  },
  { 
    book: "The Day Everything Froze", 
    img: "/schoolproject/SchoolProjectsLittleHibba3.png", 
    color: "#a18cd1",
    tag: "Interactive Adventure!"
  },
  { 
    book: "The Night Dragon", 
    img: "/schoolproject/SchoolProjectsLittleHibba4.png", 
    color: "#f6d365",
    tag: "Icy-Cool Tales!"
  },
  { 
    book: "The Battle of Badr", 
    img: "/schoolproject/SchoolProjectsLittleHibba5.png", 
    color: "#43e97b",
    tag: "Guardian of the Dark!"
  },
  { 
    book: "The Creature in the Classroom", 
    img: "/schoolproject/SchoolProjectsLittleHibba6.png", 
    color: "#fa709a",
    tag: "Historic Inspiration!"
  }
];

const useFadeInEffect = () => {
  const animatedElements = useRef([]);

  useEffect(() => {
    const elementsToObserve = animatedElements.current;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible', 'fade-in-fun');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -50px 0px' }
    );

    elementsToObserve.forEach((el) => {
      if (el) observer.observe(el);
    });

    return () => {
      elementsToObserve.forEach((el) => {
        if (el) observer.unobserve(el);
      });
      observer.disconnect();
    };
  }, []);

  const addToRefs = useCallback((el) => {
    if (el && !animatedElements.current.includes(el)) {
      animatedElements.current.push(el);
    }
  }, []);

  return addToRefs;
};

const LittleAuthors = () => {
  const addToRefs = useFadeInEffect();

  useEffect(() => {
    window.REQUIRED_CODE_ERROR_MESSAGE = 'Please choose a country code';
    window.LOCALE = 'en';
    window.EMAIL_INVALID_MESSAGE = window.SMS_INVALID_MESSAGE = "The information provided is invalid. Please review the field format and try again.";
    window.REQUIRED_ERROR_MESSAGE = "This field cannot be left blank. ";
    window.GENERIC_INVALID_MESSAGE = "The information provided is invalid. Please review the field format and try again.";
    window.INVALID_NUMBER = "The information provided is invalid. Please review the field format and try again.";
    window.INVALID_DATE = "Please enter a valid date";
    window.REQUIRED_MULTISELECT_MESSAGE = 'Please select at least 1 option';
    window.translation = {
      common: {
        selectedList: '{quantity} list selected',
        selectedLists: '{quantity} lists selected',
        selectedOption: '{quantity} selected',
        selectedOptions: '{quantity} selected',
      }
    };
    window.AUTOHIDE = Boolean(0);

    const brevoScript = document.createElement('script');
    brevoScript.src = 'https://sibforms.com/forms/end-form/build/main.js';
    brevoScript.defer = true;
    document.body.appendChild(brevoScript);

    return () => {
      if (document.body.contains(brevoScript)) {
        document.body.removeChild(brevoScript);
      }
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Little Authors | Little Hibba</title>
        <meta name="description" content="Meet our winning little authors from the Author to Entrepreneur program." />
        <link rel="stylesheet" href="https://sibforms.com/forms/end-form/build/sib-styles.css" />
      </Helmet>

      {/* --- SECTION 1: HERO --- */}
      <section className="hero-section bg-school-pattern">
        <Container>
          <Row className="align-items-center justify-content-center text-center">
            <Col lg={9} className="invisible-start" ref={addToRefs}>
              <span className="badge-fun mb-4 d-inline-block" style={{ backgroundColor: '#ff4757' }}>
                Little Authors, Big Entrepreneurs Program
              </span>
              <h1 className="display-3 fun-heading mb-4" style={{ color: '#2f3542' }}>
                Meet our winning <span style={{ color: '#1e90ff' }}>little Authors!</span>
              </h1>
              <p className="text-reading mx-auto mb-4" style={{ maxWidth: '800px', fontSize: '1.3rem' }}>
                We are absolutely thrilled to showcase the incredible talent from <strong>Cardiff Muslim Primary School</strong>. 
                These young visionaries participated in our 'Author to Entrepreneur' program, writing, illustrating, 
                and publishing their very own books!
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* --- SECTION 2: MEET THE AUTHORS GALLERY --- */}
      <section className="py-5 bg-chalkboard">
        <Container className="py-5">
          <div className="text-center mb-5 invisible-start" ref={addToRefs}>
            <h2 className="fun-heading display-4 chalk-text mb-3">Our Shining Stars 🌟</h2>
            <p className="chalk-text fs-5" style={{ opacity: 0.9 }}>
              Take a look at the amazing books created by our talented students!
            </p>
          </div>

          <Row className="g-4 justify-content-center">
            {AUTHORS.map((author, index) => (
              <Col xs={12} sm={6} lg={4} key={index} className="invisible-start" ref={addToRefs}>
                <Card className="kid-card h-100" style={{ borderColor: author.color }}>
                  <Card.Img variant="top" src={author.img} className="kid-img" alt={author.book} />
                  <Card.Body className="text-center p-4">
                    <h4 className="fun-heading mb-3" style={{ color: '#2f3542', fontSize: '1.2rem' }}>
                      "{author.book}"
                    </h4>
                    <span 
                      className="d-inline-block px-3 py-1 rounded-pill" 
                      style={{ backgroundColor: `${author.color}20`, color: author.color, fontWeight: 'bold' }}
                    >
                      {author.tag}
                    </span>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* --- SECTION 3: THE JOURNEY --- */}
      <section className="py-5" style={{ backgroundColor: '#fffbe3' }}>
        <Container className="py-5">
          <Row className="align-items-center">
            <Col lg={6} className="invisible-start mb-4 mb-lg-0" ref={addToRefs}>
              <h2 className="fun-heading display-4 mb-4" style={{ color: '#ff6b81' }}>The Journey 🚀</h2>
              <p className="text-reading mb-3">
                Our <strong>Author to Entrepreneur</strong> program takes students on an exciting adventure. 
                They don't just write a story; they learn how to structure it, illustrate it, and understand 
                what it takes to bring a real product to the world.
              </p>
              <ul className="text-reading" style={{ listStyleType: 'none', paddingLeft: 0 }}>
                <li className="mb-2">✏️ <strong>Step 1:</strong> Brainstorming & Storyboarding</li>
                <li className="mb-2">🎨 <strong>Step 2:</strong> Illustrating the magic</li>
                <li className="mb-2">📖 <strong>Step 3:</strong> Publishing the final book</li>
                <li className="mb-2">💼 <strong>Step 4:</strong> Learning to share it like an entrepreneur!</li>
              </ul>
            </Col>
            <Col lg={6} className="invisible-start" ref={addToRefs}>
              <div className="p-3 bg-white rounded-4 shadow-lg" style={{ transform: 'rotate(2deg)' }}>
                <Row className="g-2">
                  <Col xs={6}><img src="/schoolproject/SchoolProjectsLittleHibba4.png" alt="Student Work" className="img-fluid rounded-3" /></Col>
                  <Col xs={6}><img src="/schoolproject/SchoolProjectsLittleHibba5.png" alt="Student Work" className="img-fluid rounded-3" /></Col>
                </Row>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* --- SECTION 4: CALL TO ACTION (BREVO FORM) --- */}
      <section className="py-5 bg-school-pattern text-center">
        <Container className="py-5 invisible-start" ref={addToRefs}>
          <h2 className="fun-heading display-4 mb-4" style={{ color: '#1e90ff' }}>Bring This Program To Your School!</h2>
          <p className="text-reading mx-auto mb-5" style={{ maxWidth: '600px' }}>
            Empower your students to become published authors. Let's nurture the next generation of creative thinkers and young entrepreneurs together.
          </p>

          <div className="sib-form mx-auto" style={{ backgroundColor: 'transparent' }}>
            <div id="sib-form-container" className="sib-form-container">
              
              <div id="error-message" className="sib-form-message-panel" style={{ fontFamily: 'Quicksand, sans-serif', fontSize: '16px', textAlign: 'left', color: '#661d1d', backgroundColor: '#ffeded', borderColor: '#ff4949', borderRadius: '12px', maxWidth: '600px', margin: '0 auto 15px auto', padding: '15px' }}>
                <div className="sib-form-message-panel__text sib-form-message-panel__text--center">
                  <span className="sib-form-message-panel__inner-text">Your subscription could not be saved. Please try again.</span>
                </div>
              </div>

              <div id="success-message" className="sib-form-message-panel" style={{ fontFamily: 'Quicksand, sans-serif', fontSize: '16px', textAlign: 'left', color: '#085229', backgroundColor: '#e7faf0', borderColor: '#13ce66', borderRadius: '12px', maxWidth: '600px', margin: '0 auto 15px auto', padding: '15px' }}>
                <div className="sib-form-message-panel__text sib-form-message-panel__text--center">
                  <span className="sib-form-message-panel__inner-text">Your inquiry has been submitted. Please check your inbox for the confirmation email.</span>
                </div>
              </div>

              <div id="sib-container" className="sib-container--large sib-container--vertical custom-brevo-container" style={{ maxWidth: '600px', margin: '0 auto', direction: 'ltr' }}>
                <form id="sib-form" method="POST" action="https://5c5ee6ce.sibforms.com/serve/MUIFAGlYPzKtj0VWWB1hxI6s_zUzLhpuG59LTVqma_Tka-jdD575srjb7iq4VLxH9zorMHqodsEf03eMVcg6rQgG5xht19ESzBbFLNY1SxP806qQKei2RyuFFWA9I4MErUfMCYUbTQcwBOHzDeWD9Gb8nIdjHzyL7PXCLAnBE7WgCavOdbjYQ565exxM9QPKLbAN6jauQqUH62QT" data-type="subscription">

                  <div className="sib-input sib-form-block">
                    <div className="form__entry entry_block">
                      <div className="form__label-row">
                        <label className="entry__label" htmlFor="FIRSTNAME" data-required="*">First Name</label>
                        <div className="entry__field">
                          <input className="input" maxLength="200" type="text" id="FIRSTNAME" name="FIRSTNAME" autoComplete="off" placeholder="e.g. Sarah" data-required="true" required />
                        </div>
                      </div>
                      <label className="entry__error entry__error--primary"></label>
                    </div>
                  </div>

                  <div className="sib-input sib-form-block">
                    <div className="form__entry entry_block">
                      <div className="form__label-row">
                        <label className="entry__label" htmlFor="EMAIL" data-required="*">Work Email</label>
                        <div className="entry__field">
                          <input className="input" type="email" id="EMAIL" name="EMAIL" autoComplete="off" placeholder="you@school.edu" data-required="true" required />
                        </div>
                      </div>
                      <label className="entry__error entry__error--primary"></label>
                    </div>
                  </div>

                  <div className="sib-input sib-form-block">
                    <div className="form__entry entry_block">
                      <div className="form__label-row">
                        <label className="entry__label" htmlFor="SCHOOL_OR_INSTITUTION_NAME" data-required="*">School or Institution Name</label>
                        <div className="entry__field">
                          <input className="input" maxLength="200" type="text" id="SCHOOL_OR_INSTITUTION_NAME" name="SCHOOL_OR_INSTITUTION_NAME" autoComplete="off" placeholder="e.g. Cardiff Muslim Primary" data-required="true" required />
                        </div>
                      </div>
                      <label className="entry__error entry__error--primary"></label>
                    </div>
                  </div>

                  {/* FIXED PHONE FIELD: Renamed container classes to stop Brevo's JS from injecting the full country ul list */}
                  <div className="custom-sms-field sib-form-block">
                    <div className="form__entry entry_block">
                      <div className="form__label-row">
                        <label className="entry__label" htmlFor="LANDLINE_NUMBER" data-required="*">Phone Number</label>
                        <div className="custom-sms-wrapper" style={{ direction: 'ltr' }}>
                          <div className="custom-sms-input">
                            <div className="entry__field">
                              <select className="input form-select" name="LANDLINE_NUMBER__COUNTRY_CODE" data-required="true" defaultValue="+44">
                                <option value="+44">+44 UK</option>
                                <option value="+1">+1 US</option>
                                <option value="+90">+90 TR</option>
                                <option value="+353">+353 IE</option>
                                <option value="+61">+61 AU</option>
                                <option value="+971">+971 AE</option>
                                <option value="+27">+27 ZA</option>
                              </select>
                            </div>
                            <div className="entry__field">
                              <input type="tel" className="input" id="LANDLINE_NUMBER" name="LANDLINE_NUMBER" autoComplete="off" placeholder="7123 456789" data-required="true" required />
                            </div>
                          </div>
                        </div>
                      </div>
                      <label className="entry__error entry__error--primary"></label>
                      <label className="entry__error entry__error--secondary"></label>
                    </div>
                  </div>

                  <div className="sib-input sib-form-block">
                    <div className="form__entry entry_block">
                      <div className="form__label-row">
                        <label className="entry__label" htmlFor="LOCATION_CITY" data-required="*">Location / City</label>
                        <div className="entry__field">
                          <input className="input" maxLength="200" type="text" id="LOCATION_CITY" name="LOCATION_CITY" autoComplete="off" placeholder="e.g. London" data-required="true" required />
                        </div>
                      </div>
                      <label className="entry__error entry__error--primary"></label>
                    </div>
                  </div>

                  <div className="sib-checkbox-group sib-form-block" data-required="true">
                    <div className="form__entry entry_mcq">
                      <div className="form__label-row">
                        <label className="entry__label" data-required="*">Which age group/year groups are you looking to run this for?</label>
                        <div>
                          <div className="entry__choice">
                            <label className="checkbox__label">
                              <input type="checkbox" className="input_replaced" name="WHICH_AGE_GROUPS_ARE_YOU_LOOKING_TO_RUN_THIS_FOR[]" data-value="Key Stage 1 (Ages 5–7)" value="Key Stage 1 (Ages 5–7)" data-required="true" />
                              <span className="checkbox checkbox_tick_positive"></span><span>Key Stage 1 (Ages 5–7)</span>
                            </label>
                          </div>
                          <div className="entry__choice">
                            <label className="checkbox__label">
                              <input type="checkbox" className="input_replaced" name="WHICH_AGE_GROUPS_ARE_YOU_LOOKING_TO_RUN_THIS_FOR[]" data-value="Key Stage 2 (Ages 7–11)" value="Key Stage 2 (Ages 7–11)" data-required="true" />
                              <span className="checkbox checkbox_tick_positive"></span><span>Key Stage 2 (Ages 7–11)</span>
                            </label>
                          </div>
                          <div className="entry__choice">
                            <label className="checkbox__label">
                              <input type="checkbox" className="input_replaced" name="WHICH_AGE_GROUPS_ARE_YOU_LOOKING_TO_RUN_THIS_FOR[]" data-value="Key Stage 3+ (Ages 11+)" value="Key Stage 3+ (Ages 11+)" data-required="true" />
                              <span className="checkbox checkbox_tick_positive"></span><span>Key Stage 3+ (Ages 11+)</span>
                            </label>
                          </div>
                          <div className="entry__choice">
                            <label className="checkbox__label">
                              <input type="checkbox" className="input_replaced" name="WHICH_AGE_GROUPS_ARE_YOU_LOOKING_TO_RUN_THIS_FOR[]" data-value="Other" value="Other" data-required="true" />
                              <span className="checkbox checkbox_tick_positive"></span><span>Other</span>
                            </label>
                          </div>
                        </div>
                      </div>
                      <label className="entry__error entry__error--primary"></label>
                    </div>
                  </div>

                  <div className="sib-checkbox-group sib-form-block" data-required="true">
                    <div className="form__entry entry_mcq">
                      <div className="form__label-row">
                        <label className="entry__label" data-required="*">Approximate number of students participating</label>
                        <div>
                          <div className="entry__choice">
                            <label className="checkbox__label">
                              <input type="checkbox" className="input_replaced" name="APPROXIMATE_NUMBER_OF_STUDENTS_PARTICIPATING[]" data-value="Under 30 students" value="Under 30 students" data-required="true" />
                              <span className="checkbox checkbox_tick_positive"></span><span>Under 30 students</span>
                            </label>
                          </div>
                          <div className="entry__choice">
                            <label className="checkbox__label">
                              <input type="checkbox" className="input_replaced" name="APPROXIMATE_NUMBER_OF_STUDENTS_PARTICIPATING[]" data-value="30–60 students" value="30–60 students" data-required="true" />
                              <span className="checkbox checkbox_tick_positive"></span><span>30–60 students</span>
                            </label>
                          </div>
                          <div className="entry__choice">
                            <label className="checkbox__label">
                              <input type="checkbox" className="input_replaced" name="APPROXIMATE_NUMBER_OF_STUDENTS_PARTICIPATING[]" data-value="60–100 students" value="60–100 students" data-required="true" />
                              <span className="checkbox checkbox_tick_positive"></span><span>60–100 students</span>
                            </label>
                          </div>
                          <div className="entry__choice">
                            <label className="checkbox__label">
                              <input type="checkbox" className="input_replaced" name="APPROXIMATE_NUMBER_OF_STUDENTS_PARTICIPATING[]" data-value="100+ students" value="100+ students" data-required="true" />
                              <span className="checkbox checkbox_tick_positive"></span><span>100+ students</span>
                            </label>
                          </div>
                        </div>
                      </div>
                      <label className="entry__error entry__error--primary"></label>
                    </div>
                  </div>

                  <div className="sib-checkbox-group sib-form-block" data-required="true">
                    <div className="form__entry entry_mcq">
                      <div className="form__label-row">
                        <label className="entry__label" data-required="*">When would you ideally like to launch this program?</label>
                        <div>
                          <div className="entry__choice">
                            <label className="checkbox__label">
                              <input type="checkbox" className="input_replaced" name="WHEN_WOULD_YOU_IDEALLY_LIKE_TO_LAUNCH_THIS_PROGRAM[]" data-value="Next Term (Autumn)" value="Next Term (Autumn)" data-required="true" />
                              <span className="checkbox checkbox_tick_positive"></span><span>Next Term (Autumn)</span>
                            </label>
                          </div>
                          <div className="entry__choice">
                            <label className="checkbox__label">
                              <input type="checkbox" className="input_replaced" name="WHEN_WOULD_YOU_IDEALLY_LIKE_TO_LAUNCH_THIS_PROGRAM[]" data-value="Spring Term" value="Spring Term" data-required="true" />
                              <span className="checkbox checkbox_tick_positive"></span><span>Spring Term</span>
                            </label>
                          </div>
                          <div className="entry__choice">
                            <label className="checkbox__label">
                              <input type="checkbox" className="input_replaced" name="WHEN_WOULD_YOU_IDEALLY_LIKE_TO_LAUNCH_THIS_PROGRAM[]" data-value="Summer Term" value="Summer Term" data-required="true" />
                              <span className="checkbox checkbox_tick_positive"></span><span>Summer Term</span>
                            </label>
                          </div>
                          <div className="entry__choice">
                            <label className="checkbox__label">
                              <input type="checkbox" className="input_replaced" name="WHEN_WOULD_YOU_IDEALLY_LIKE_TO_LAUNCH_THIS_PROGRAM[]" data-value="Flexible / Just exploring options" value="Flexible / Just exploring options" data-required="true" />
                              <span className="checkbox checkbox_tick_positive"></span><span>Flexible / Just exploring options</span>
                            </label>
                          </div>
                        </div>
                      </div>
                      <label className="entry__error entry__error--primary"></label>
                    </div>
                  </div>

                  <div className="sib-checkbox-group sib-form-block" data-required="true">
                    <div className="form__entry entry_mcq">
                      <div className="form__label-row">
                        <label className="entry__label" data-required="*">What is your main goal for bringing the "Author to Entrepreneur" program to your students?</label>
                        <div>
                          <div className="entry__choice">
                            <label className="checkbox__label">
                              <input type="checkbox" className="input_replaced" name="WHAT_IS_THE_MAIN_GOAL_FOR_THIS_PROGRAM[]" data-value="Boosting creative writing & literacy skills" value="Boosting creative writing & literacy skills" data-required="true" />
                              <span className="checkbox checkbox_tick_positive"></span><span>Boosting creative writing & literacy skills</span>
                            </label>
                          </div>
                          <div className="entry__choice">
                            <label className="checkbox__label">
                              <input type="checkbox" className="input_replaced" name="WHAT_IS_THE_MAIN_GOAL_FOR_THIS_PROGRAM[]" data-value="Developing entrepreneurial and business thinking" value="Developing entrepreneurial and business thinking" data-required="true" />
                              <span className="checkbox checkbox_tick_positive"></span><span>Developing entrepreneurial and business thinking</span>
                            </label>
                          </div>
                          <div className="entry__choice">
                            <label className="checkbox__label">
                              <input type="checkbox" className="input_replaced" name="WHAT_IS_THE_MAIN_GOAL_FOR_THIS_PROGRAM[]" data-value="Building student confidence through tangible achievements" value="Building student confidence through tangible achievements" data-required="true" />
                              <span className="checkbox checkbox_tick_positive"></span><span>Building student confidence through tangible achievements</span>
                            </label>
                          </div>
                          <div className="entry__choice">
                            <label className="checkbox__label">
                              <input type="checkbox" className="input_replaced" name="WHAT_IS_THE_MAIN_GOAL_FOR_THIS_PROGRAM[]" data-value="Creating a unique showcase/publishing project for the school" value="Creating a unique showcase/publishing project for the school" data-required="true" />
                              <span className="checkbox checkbox_tick_positive"></span><span>Creating a unique showcase/publishing project for the school</span>
                            </label>
                          </div>
                        </div>
                      </div>
                      <label className="entry__error entry__error--primary"></label>
                    </div>
                  </div>

                  <div className="sib-input sib-form-block">
                    <div className="form__entry entry_block">
                      <div className="form__label-row">
                        <label className="entry__label" htmlFor="IS_THERE_ANY_SPECIFIC_DETAIL_OR_UNIQUE_REQUIREMENT" data-required="*">Is there any specific detail or unique requirement about your school or students we should know?</label>
                        <div className="entry__field">
                          <input className="input" maxLength="200" type="text" id="IS_THERE_ANY_SPECIFIC_DETAIL_OR_UNIQUE_REQUIREMENT" name="IS_THERE_ANY_SPECIFIC_DETAIL_OR_UNIQUE_REQUIREMENT" autoComplete="off" placeholder="Write here..." data-required="true" required />
                        </div>
                      </div>
                      <label className="entry__error entry__error--primary"></label>
                    </div>
                  </div>

                  <div style={{ marginTop: '30px' }}>
                    <button 
                      className="sib-form-block__button sib-form-block__button-with-loader custom-submit-btn" 
                      form="sib-form" 
                      type="submit"
                    >
                      REGISTER MY INTEREST 📚
                    </button>
                  </div>

                  <input type="text" name="email_address_check" defaultValue="" className="input--hidden" style={{ display: 'none' }} />
                  <input type="hidden" name="locale" defaultValue="en" />
                </form>
              </div>
            </div>
          </div>

        </Container>
      </section>
    </>
  );
};

export default LittleAuthors;